import { CareerGoalsPreferences, CareerProfile, EarlyCareerTalent } from '../types';
import { getIdToken } from '../auth-config';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://127.0.0.1:8001');

class ApiService {
  private getCurrentUserAlias(): string | null {
    const alias = (window as any).__currentUserAlias;
    return alias || null;
  }

  setCurrentUserAlias(alias: string) {
    (window as any).__currentUserAlias = alias;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const userAlias = this.getCurrentUserAlias();
    const idToken = getIdToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(userAlias && { 'x-user-alias': userAlias }),
      ...(idToken && { Authorization: `Bearer ${idToken}` }),
      ...options?.headers,
    };

    try {
      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Career Goals & Preferences
  async getCareerGoals(directReportAlias?: string): Promise<CareerGoalsPreferences> {
    const url = directReportAlias
      ? `/api/v1/career-goals?direct_report_alias=${directReportAlias}`
      : `/api/v1/career-goals`;
    return this.request<CareerGoalsPreferences>(url);
  }

  async saveCareerGoals(data: Omit<CareerGoalsPreferences, 'id' | 'alias'>): Promise<CareerGoalsPreferences> {
    return this.request<CareerGoalsPreferences>('/api/v1/career-goals', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Career Profile (Manager view)
  async getCareerProfile(directReportAlias: string): Promise<CareerProfile> {
    return this.request<CareerProfile>(`/api/v1/career-profile?direct_report_alias=${directReportAlias}`);
  }

  async saveCareerProfile(directReportAlias: string, data: Omit<CareerProfile, 'id' | 'alias' | 'manager_alias'>): Promise<CareerProfile> {
    return this.request<CareerProfile>(`/api/v1/career-profile?direct_report_alias=${directReportAlias}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Early Career Talent (Manager view)
  async getEarlyCareerTalent(directReportAlias: string): Promise<EarlyCareerTalent> {
    return this.request<EarlyCareerTalent>(`/api/v1/early-career-talent?direct_report_alias=${directReportAlias}`);
  }

  async saveEarlyCareerTalent(directReportAlias: string, data: Omit<EarlyCareerTalent, 'id' | 'alias' | 'manager_alias'>): Promise<EarlyCareerTalent> {
    return this.request<EarlyCareerTalent>(`/api/v1/early-career-talent?direct_report_alias=${directReportAlias}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Direct Reports
  async getDirectReports(): Promise<any> {
    return this.request<any>(`/api/v1/direct-reports`);
  }

  // AI Summarization
  async getAiStatus(): Promise<{ ai_enabled: boolean }> {
    return this.request<{ ai_enabled: boolean }>('/api/v1/ai/status');
  }

  async summarizeText(
    text: string,
    contextType: string = 'highlight',
  ): Promise<{ summary: string; feedback?: string }> {
    return this.request<{ summary: string; feedback?: string }>('/api/v1/ai/summarize', {
      method: 'POST',
      body: JSON.stringify({
        text,
        context_type: contextType,
      }),
    });
  }
}

export const api = new ApiService();
